import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import Layout from "../../layout";
import axios from "axios";
import {
  ConsumptionTrendChart,
  TopConsumersChart,
  LoadProfileHeatmap,
  StatCard,
} from "./components/ReportsV2Charts";
import { useNavigate } from "react-router-dom";

const ConsumptionV2: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const [meters, setMeters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  // Filter states
  const [selectedMeter, setSelectedMeter] = useState("all");
  const [fromDate, setFromDate] = useState("2026-03-01");
  const [toDate, setToDate] = useState("2026-03-27");

  const [data, setData] = useState({
    consumptionTrend: [] as any[],
    topConsumers: [] as any[],
    loadProfile: [] as any[],
  });

  // Derived stats
  const totalConsumption = data.consumptionTrend.reduce(
    (acc, curr) => acc + curr.totalKwh,
    0,
  );
  const avgDaily =
    data.consumptionTrend.length > 0
      ? totalConsumption / data.consumptionTrend.length
      : 0;
  const peakDayObj =
    data.consumptionTrend.length > 0
      ? [...data.consumptionTrend].sort((a, b) => b.totalKwh - a.totalKwh)[0]
      : { totalKwh: 0, date: "" };
  const daysCovered = data.consumptionTrend.length;

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/reports-v2/overview");
    if (newValue === 1) navigate("/reports-v2/consumption");
    if (newValue === 2) navigate("/reports-v2/revenue-billing");
    if (newValue === 3) navigate("/reports-v2/billing-analytics");
  };

  const fetchMeters = async () => {
    try {
      const response = await axios.get(
        "https://backend.voltreum.com/api/v2/meters",
      );
      if (response.data.success) {
        setMeters(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching meters:", error);
    }
  };

  const fetchReportData = async () => {
    setFetching(true);
    try {
      let queryParams = `from=${fromDate}&to=${toDate}`;
      if (selectedMeter && selectedMeter !== "all") {
        queryParams += `&meterSerial=${selectedMeter}`;
      }

      const [trendRes, topRes, loadRes] = await Promise.all([
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/consumption-trend?${queryParams}`,
        ),
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/top-consumers?top=10&${queryParams}`,
        ),
        axios.get(
          `https://backend.voltreum.com/api/v2/reports/load-profile?${queryParams}`,
        ),
      ]);

      setData({
        consumptionTrend: trendRes.data.success ? trendRes.data.data : [],
        topConsumers: topRes.data.success ? topRes.data.data : [],
        loadProfile: loadRes.data.success ? loadRes.data.data : [],
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchMeters();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (selectedMeter) {
      fetchReportData();
    }
  }, [selectedMeter, fromDate, toDate]);

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 0, minHeight: "100vh", color: "#181B25" }}>
        <Box sx={{ mb: 3, pt: 0, px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                minHeight: "48px",
              },
              "& .Mui-selected": { color: "#1064ED !important" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1064ED",
                height: "3px",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Consumption" />
            <Tab label="Revenue & Billing" />
            <Tab label="Billing Analytics" />
            <Tab label="Power Quality" />
            <Tab label="Predictive Analytics" />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, pb: 4 }}>
          {/* Header Title */}
          {/* <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)", fontWeight: 700, mb: 2 }}>Reports & Analytics</Typography> */}

          {/* Filter Bar */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #D3E3FD",
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedMeter}
                      onChange={(e) => setSelectedMeter(e.target.value)}
                      sx={{ borderRadius: "6px", backgroundColor: "#F3F6FC" }}
                    >
                      <MenuItem value="all">All Meters</MenuItem>
                      {meters.map((m) => (
                        <MenuItem key={m.id} value={m.meterNumber}>
                          {m.meterNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { backgroundColor: "#F3F6FC" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { backgroundColor: "#F3F6FC" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={fetchReportData}
                    sx={{
                      backgroundColor: "#5E5CE6",
                      color: "#FFF",
                      borderRadius: "6px",
                      textTransform: "none",
                      boxShadow: "none",
                    }}
                  >
                    Apply
                  </Button>
                </Grid>
                <Grid item xs={6} md={1}>
                  {fetching && <CircularProgress size={20} />}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Metric Cards Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="TOTAL CONSUMPTION"
                value={`${(totalConsumption / 1000).toFixed(1)}K kWh`}
                subValue="TOTAL CONSUMPTION"
                color="#1064ED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="AVG DAILY"
                value={`${(avgDaily / 1000).toFixed(1)}K kWh`}
                subValue="AVG DAILY"
                color="#7C3AED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="PEAK DAY"
                value={`${(peakDayObj.totalKwh / 1000).toFixed(1)}K kWh`}
                subValue={
                  peakDayObj.date
                    ? new Date(peakDayObj.date).toLocaleDateString()
                    : "-"
                }
                color="#EF4444"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="DAYS COVERED"
                value={daysCovered}
                subValue="DAYS COVERED"
                color="#10B981"
              />
            </Grid>
          </Grid>

          {/* Daily Consumption Trend */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Card
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "10px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #D3E3FD",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    mb: 2,
                    fontWeight: 700,
                    fontSize: "16px",
                  }}
                >
                  Daily Consumption Trend
                </Typography>
                <ConsumptionTrendChart data={data.consumptionTrend} />
              </CardContent>
            </Card>
          </Grid>

          <Grid container spacing={3}>
            {/* Top Consumers */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Top Consumers
                  </Typography>
                  <TopConsumersChart data={data.topConsumers} />
                </CardContent>
              </Card>
            </Grid>

            {/* Load Profile Heatmap */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #D3E3FD",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      mb: 2,
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    Load Profile Heatmap (Avg kWh by Hour & Day)
                  </Typography>
                  <LoadProfileHeatmap data={data.loadProfile} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Layout>
  );
};

export default ConsumptionV2;
